'use client';
import classNames from 'classnames';
import Link from 'next/link';
import {
  AriaAttributes,
  MouseEventHandler,
  ReactNode,
  forwardRef,
} from 'react';
import styles from './UIButton.module.scss';

type UIButtonProps = AriaAttributes & {
  buttonType: 'primary' | 'secondary' | 'tertiary' | 'tonal' | 'text';
  label: string;
  fullWidth?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
  id?: string;
  'data-testid'?: string;
  // aria-label is required when label is '' (icon-only) to keep the button accessible
  href?: string;
  prefetch?: boolean;
  disabled?: boolean;
  htmlType?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

const UIButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  UIButtonProps
>((props, ref) => {
  const {
    buttonType,
    className,
    label,
    fullWidth,
    icon,
    iconPosition = 'right',
    href,
    prefetch,
    disabled,
    htmlType = 'button',
    onClick,
    ...rest
  } = props;

  const iconClass = icon
    ? iconPosition === 'left'
      ? styles.iconLeft
      : styles.iconRight
    : undefined;

  const combinedClassName = classNames(
    styles['ui-button'],
    styles[buttonType],
    iconClass,
    label === '' && styles.iconOnly,
    fullWidth && styles.fullWidth,
    className
  );

  const content = (
    <>
      {iconPosition === 'left' && icon}
      {label}
      {iconPosition === 'right' && icon}
    </>
  );

  if (href) {
    return (
      <Link
        {...rest}
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        prefetch={prefetch}
        className={combinedClassName}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      {...rest}
      ref={ref as React.Ref<HTMLButtonElement>}
      type={htmlType}
      className={combinedClassName}
      disabled={disabled}
      onClick={onClick}
    >
      {content}
    </button>
  );
});

UIButton.displayName = 'UIButton';

export default UIButton;
